import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { getSession, setSession, clearSession, getToken } from "./utils/session";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";

const ProtectedRoute = ({ children }) => {
  const location = useLocation();
  // "checking" | "authorized" | "unauthorized"
  const [status, setStatus] = useState(() => (getSession() ? "checking" : "unauthorized"));

  useEffect(() => {
    let cancelled = false;

    const verify = async () => {
      const session = getSession();
      if (!session) {
        if (!cancelled) setStatus("unauthorized");
        return;
      }

      setStatus("checking");

      try {
        const token = getToken();
        const res = await fetch(`${API_BASE_URL}/api/auth/verify/`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!res.ok) {
          clearSession();
          if (!cancelled) setStatus("unauthorized");
          return;
        }

        const data = await res.json();
        if (data?.email && data.email !== session.email) {
          setSession({ email: data.email });
        }

        if (!cancelled) setStatus("authorized");
      } catch (err) {
        clearSession();
        if (!cancelled) setStatus("unauthorized");
      }
    };

    verify();

    return () => {
      cancelled = true;
    };
    // Re-verify whenever the route changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // On any invalidation, force a hard reload to "/" rather than a client-side
  // <Navigate>. App.js only computes `isAuthenticated` once (at mount / via
  // its own login/logout handlers) — a client-side redirect here would leave
  // that stale `true` value in place, so the route table would immediately
  // re-mount this same ProtectedRoute instead of showing the login screen,
  // producing an infinite redirect loop instead of returning to login.
  useEffect(() => {
    if (status === "unauthorized") {
      window.location.replace("/");
    }
  }, [status]);

  if (status === "authorized") {
    return children;
  }

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        fontFamily: "'Inter', 'DM Sans', system-ui, sans-serif",
        color: "#64748b",
        fontSize: "14px",
        fontWeight: 600,
      }}
    >
      {status === "checking" ? "Verifying session…" : null}
    </div>
  );
};

export default ProtectedRoute;
